import * as React from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridToolbar,
  GridCustomFilterCellProps,
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridSelectableMode,
  isColumnMenuSortActive,
  isColumnMenuFilterActive,
} from '@progress/kendo-react-grid';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { GridRowAssetTableInfo } from '../../../Interfaces/Assets/AssetInfo';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import {
  getGroupIds,
  setExpandedState,
  setGroupIds,
  Pager,
  PageChangeEvent,
  SelectDescriptor,
  getSelectedState,
} from '@progress/kendo-react-data-tools';
import { DataResult, process, State } from '@progress/kendo-data-query';
import {
  saveNodesConfigGridState,
  saveNodesJsonConfigGridState,
} from '../../../store/UserSettings/UserSettingsActions';
import { Dispatch } from 'redux';
import { useEffect } from 'react';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  LocalizationProvider,
  loadMessages,
  load,
  IntlProvider,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../../AlertsGrid/GridLocale';
import { Button } from '@progress/kendo-react-buttons';
import { getLocalDateTime, flattenNodeConfigData } from '../../../utils/utils';
import { LoadingPanel } from '../../LoadingPanel';
import {
  exportFilteredAssetsConfig,
  getAllNodesConfigByPage,
  resetNodesResult,
  resetFilteredAssets,
  resetFetchError,
  clearEditConfigSettings,
} from '../../../store/Config/Settings/ConfigSettingsActions';
import NodeConfigFilter from './NodeConfigFilter';
import NodeConfigFormContainer from './NodeConfigFormContainer';
import { FetchError } from '../../../Interfaces/FetchError';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import { CSVLink } from 'react-csv';
import { dateFormat } from '../../../utils/utils';
import { initialNodesConfigGridDataState } from '../../../store/UserSettings/UserSettingsState';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { Splitter, SplitterOnChangeEvent } from '@progress/kendo-react-layout';
import StringResourceManager, { GetString } from '../../StringResourceManager';
import esMessages from '../../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import weekData from 'cldr-core/supplemental/weekData.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import caGregorian from 'cldr-dates-full/main/fr/ca-gregorian.json';
import numbers from 'cldr-numbers-full/main/fr/numbers.json';
import dateFields from 'cldr-dates-full/main/fr/dateFields.json';
import timeZones from 'cldr-dates-full/main/fr/timeZoneNames.json';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from '@progress/kendo-data-query';
import { CustomDateFilterCell } from '../../CustomDateFilterCell';
import {
  NodeConfigInfo,
  NodeConfigQueryResult,
} from '../../../Interfaces/Config/NodeConfigInfo';
import { getNodeConfigCategoryList } from '../../../store/Config/Settings/ConfigSettingsActions';
import { ConfigSetting } from '../../../Interfaces/Config/ConfigSetting';
import { NodeInfo } from '../../../Interfaces/Assets/AssetInfo';
import { Dialog } from '@progress/kendo-react-dialogs';
import EditNodeConfigDetailComponent from './EditNodeConfigDetailComponent';
load(
  likelySubtags,
  currencyData,
  weekData,
  caGregorian,
  numbers,
  dateFields,
  timeZones,
);
import { selectionModes } from '../../../components/AlertsGrid/GridRowSelectionMode';
import {
  ColumnMenu,
  CustomCell,
  CustomColumnMenu,
  HeaderCustomCell,
} from '../../ColumnMenus/ColumnMenu';
import { ApiEnums } from '../../../Interfaces/ApiEnums';

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    laneCount: {
      fontWeight: 'bold',
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    error: {
      fontSize: '1.5rem',
    },
    actionButton: {
      backgroundColor: '#153654',
      width: '150px',
      color: '#fff',
      '&:hover': {
        background: '#153654',
        color: '#fff',
      },
    },
    send: {
      fontSize: '1.5rem',
    },
  }),
);
interface Props {
  setLastRefreshQuery: (lastRefreshDate: Date) => void;
  configSettingType: ApiEnums.ConfigSettingsType;
}

interface NodesConfigGridResult {
  NodeInfo: NodeInfo;
  ConfigSettingList: ConfigSetting[];
  Selected: boolean;
}

interface UseNodeConfigGridContextResult {
  activeDataState: State;
  activeFilters: CompositeFilterDescriptor;
  saveActiveGridState: (newDataState: State) => void;
}

const DATA_ITEM_KEY = 'NodeInfo.NodeID';

const useNodeConfigGridContext = (
  configSettingType: ApiEnums.ConfigSettingsType,
): UseNodeConfigGridContextResult => {
  const dispatch: Dispatch<any> = useDispatch();

  const registryDataState: State = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.nodesConfigGridDataState,
  );

  const jsonDataState: State = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.nodesJsonConfigGridDataState,
  );

  const registryFilters: CompositeFilterDescriptor = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.nodesConfigFilters,
  );

  const jsonFilters: CompositeFilterDescriptor = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.nodesJsonConfigFilters,
  );

  const isRegistrySetting =
    configSettingType === ApiEnums.ConfigSettingsType.RegistrySetting;

  const activeDataState = isRegistrySetting ? registryDataState : jsonDataState;

  const activeFilters = isRegistrySetting ? registryFilters : jsonFilters;

  const saveActiveGridState = React.useCallback(
    (newDataState: State): void => {
      if (isRegistrySetting) {
        saveNodesConfigGridState(dispatch, newDataState);
      } else {
        saveNodesJsonConfigGridState(dispatch, newDataState);
      }
    },
    [dispatch, isRegistrySetting],
  );

  return {
    activeDataState,
    activeFilters,
    saveActiveGridState,
  };
};

const DateTimeCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  if (
    dataItem?.NodeInfo !== undefined &&
    dataItem.NodeInfo.LastHeartbeat !== '0001-01-01T00:00:00'
  ) {
    return <td>{getLocalDateTime(dataItem.NodeInfo.LastHeartbeat)}</td>;
  }

  return <td></td>;
};

interface ViewCommandCellProps extends GridCellProps {
  enterViewNodesConfig: (item: NodeConfigInfo) => void;
}

const ViewCommandCell = (props: ViewCommandCellProps) => {
  if (props.dataIndex !== -1) {
    return (
      <td>
        <Button
          themeColor={'primary'}
          type="button"
          onClick={() => props.enterViewNodesConfig(props.dataItem)}
        >
          {GetString('VIEW')}
        </Button>
      </td>
    );
  }

  return null;
};

const NodesConfigGrid: React.FC<Props> = ({
  setLastRefreshQuery,
  configSettingType,
}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const { activeDataState, activeFilters, saveActiveGridState } =
    useNodeConfigGridContext(configSettingType);

  const status = GetString('STATUS');

  const [selectionMode, setSelectionMode] = React.useState<
    GridSelectableMode | undefined
  >(selectionModes[1].value);

  const [nodesConfigGridDataResult, setNodesConfigGridDataResult] =
    React.useState<NodesConfigGridResult[]>([]);

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const loading: boolean = useSelector(
    (state: ApplicationState) =>
      state.configSettingsReducer.isFetchingNodesSettings,
  );

  const editConfigStatus: boolean | undefined = useSelector(
    (state: ApplicationState) => state.configSettingsReducer.editConfigStatus,
  );

  const nodeQueryResult: NodeConfigQueryResult = useSelector(
    (state: ApplicationState) =>
      configSettingType === ApiEnums.ConfigSettingsType.RegistrySetting
        ? state.configSettingsReducer.nodesByPage
        : state.configSettingsReducer.jsonNodesByPage,
  );

  const filteredAssetsData: NodeConfigInfo[] = useSelector(
    (state: ApplicationState) => state.configSettingsReducer.filteredAssetsData,
  );

  const fetchError: FetchError = useSelector(
    (state: ApplicationState) => state.configSettingsReducer.fetchError,
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const [select, setSelect] = React.useState<SelectDescriptor>({});

  const [selectedNodeConfig, setSelectedNodeConfig] =
    React.useState<NodeConfigInfo>(Object);

  const [OpenNodesConfigPage, setOpenNodesConfigPage] =
    React.useState<boolean>(false);

  const [selectedNodeUuids, setSelectedNodeUuids] = React.useState<string[]>(
    [],
  );

  const [uniqueKeyOnRefresh, setUniqueKeyOnRefresh] = React.useState<Date>(
    new Date(),
  );

  const [collapsedState, setCollapsedState] = React.useState<string[]>([]);

  const [queryError, setQueryError] = React.useState<string>('');

  const [error, displayError] = React.useState(false);

  const [nodeConfigDataToExport, setNodeConfigDataToExport] = React.useState<
    GridRowAssetTableInfo[]
  >([]);

  const [verticalPanes, setVerticalPanes] = React.useState<Array<any>>([
    {},
    {},
  ]);

  const [editDialogVisible, setEditDialogVisible] =
    React.useState<boolean>(false);

  const [editConfigButtonEnabled, setEditConfigButtonEnabled] =
    React.useState<boolean>(false);

  const COLUMN_TITLE_STORE = GetString('COLUMN_TITLE_STORE');
  const COLUMN_TITLE_CLUSTER = GetString('COLUMN_TITLE_CLUSTER');
  const COLUMN_TITLE_LANE = GetString('COLUMN_TITLE_LANE');
  const COLUMN_TITLE_DIVISION = GetString('COLUMN_TITLE_DIVISION');
  const COLUMN_TITLE_LANE_TYPE = GetString('COLUMN_TITLE_LANE_TYPE');
  const COLUMN_TITLE_CONFIGURATION = GetString('CONFIGURATION');
  const COLUMN_TITLE_LANE_LAST_REFRESHED = GetString(
    'COLUMN_TITLE_LANE_LAST_REFRESHED',
  );

  const SELECTED_FIELD = 'Selected';
  const EDIT_CONFIG = GetString('EDIT_CONFIG');

  const processWithGroups = (data: any[], gridState?: State): DataResult => {
    const effectiveDataState = gridState ?? initialNodesConfigGridDataState;

    const filter: (FilterDescriptor | CompositeFilterDescriptor)[] = [];
    const subFilter: FilterDescriptor[] = [];

    const filters:
      | (CompositeFilterDescriptor | FilterDescriptor)[]
      | undefined = effectiveDataState.filter?.filters;

    filters?.forEach((x: any) => {
      const props = new Map<string, FilterDescriptor[]>(Object.entries(x));
      const newFilters: FilterDescriptor[] = props.get('filters') || [];
      const nodeTypeFilterDescriptor: FilterDescriptor[] = [];
      const nodeTypeFilter: CompositeFilterDescriptor = {
        logic: 'or',
        filters: nodeTypeFilterDescriptor,
      };

      newFilters.forEach(a => {
        if (Array.isArray(a.value)) {
          for (let i = 0; i < a.value.length; i++) {
            const newValue: FilterDescriptor = {
              field: a?.field,
              operator: a?.operator,
              value: a?.value[i],
            };
            nodeTypeFilterDescriptor.push(newValue);
          }
        }
      });

      if (x.field === 'NodeInfo.LastHeartbeat') {
        filter.push(x);
      } else {
        newFilters.forEach(a => {
          if (Array.isArray(a.value)) {
            filter.push(nodeTypeFilter);
          } else {
            filter.push(x);
          }
        });
      }
    });

    const newFilter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: filter,
    };

    if (subFilter.length > 0) {
      newFilter.filters.push({
        logic: 'or',
        filters: subFilter,
      });
    }

    const newDataStateValue: State = {
      ...effectiveDataState,
      filter: newFilter,
    };

    const newDataState: DataResult = process(data, newDataStateValue);

    setGroupIds({
      data: newDataState.data,
      group: newDataStateValue.group,
    });

    return newDataState;
  };

  const [dataResult, setDataResult] = React.useState<DataResult>(
    processWithGroups(nodesConfigGridDataResult, activeDataState),
  );

  useEffect(() => {
    setDataResult(
      processWithGroups(nodesConfigGridDataResult, activeDataState),
    );
  }, [nodesConfigGridDataResult, activeDataState, configSettingType]);

  useEffect(() => {
    let timeoutId: any;

    if (editConfigStatus) {
      fetchAllNodesConfigbyPage();

      timeoutId = setTimeout(() => {
        clearEditConfigSettings(dispatch);
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [editConfigStatus]);

  useEffect(() => {
    const queryResult: NodesConfigGridResult[] = nodeQueryResult.Result.map(
      result => {
        return {
          ...result,
          Selected: false,
        };
      },
    );

    setNodesConfigGridDataResult(queryResult);
    setSelect({});
  }, [nodeQueryResult.Result]);

  useEffect(() => {
    if (selectedLanguage === 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage === 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (filteredAssetsData.length > 0) {
      setAssetsDataToExport();
    }
  }, [filteredAssetsData]);

  useEffect(() => {
    if (nodeConfigDataToExport.length > 0 && csvLinkRef?.current) {
      csvLinkRef.current.link.click();

      resetFilteredAssets(dispatch);
    }
  }, [nodeConfigDataToExport]);

  useEffect(() => {
    let timeoutId: any;

    if (fetchError.StatusCode > 0 && fetchError.Message !== '') {
      displayError(true);

      timeoutId = setTimeout(() => {
        clearFetchError();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchError]);

  useEffect(() => {
    let timeoutId: any;

    if (queryError !== '') {
      displayError(true);

      timeoutId = setTimeout(() => {
        displayError(false);
        setQueryError('');
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [queryError]);

  useEffect(() => {
    getNodeConfigCategoryList(dispatch, configSettingType);

    const settingsQueryStatus: any = getSettingQueryStatus();

    if (settingsQueryStatus.exist && settingsQueryStatus.invalid) {
      fetchAllNodesConfigbyPage();
    }
  }, [configSettingType]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    saveActiveGridState(event.dataState);

    setDataResult(
      processWithGroups(nodesConfigGridDataResult, event.dataState),
    );
  };

  const onGroupsToggle = React.useCallback(() => {
    const dataStateWithoutPaging = processWithGroups(
      nodesConfigGridDataResult,
      {
        group: activeDataState.group,
      },
    );

    setCollapsedState(
      collapsedState.length
        ? []
        : getGroupIds({ data: dataStateWithoutPaging.data }),
    );
  }, [collapsedState, activeDataState, nodesConfigGridDataResult]);

  const newData = setExpandedState({
    data: dataResult.data,
    collapsedIds: collapsedState,
  });

  const excelExportRef = React.useRef<ExcelExport | null>(null);

  const excelExport = () => {
    if (excelExportRef.current !== null) {
      excelExportRef.current.save();
    }
  };

  const onRunFilterNodesQuery = () => {
    const newDataState: State = {
      skip: 0,
      take: activeDataState.take,
      sort: activeDataState.sort,
      group: activeDataState.group,
    };
    setUniqueKeyOnRefresh(new Date());
    saveActiveGridState(newDataState);

    const settingsQueryStatus: any = getSettingQueryStatus();

    if (!settingsQueryStatus.exist) {
      setQueryError('SETTING_FILTER_NOT_EXISTED');
    } else if (!settingsQueryStatus.invalid) {
      setQueryError('SETTING_FILTER_NOT_VALID');
    } else {
      fetchAllNodesConfigbyPage();
    }
  };

  const fetchAllNodesConfigbyPage = () => {
    if (nodesConfigGridDataResult.length > 0) {
      resetNodesResult(dispatch);
      setEditConfigButtonEnabled(false);
    }

    setLastRefreshQuery(new Date());
    getAllNodesConfigByPage(dispatch, configSettingType);
  };

  const getSettingQueryStatus = () => {
    const settingsQueryStatus: any = {
      exist: false,
      invalid: false,
    };

    activeFilters.filters.map((e: any) => {
      if (e?.field === 'ConfigSetting') {
        settingsQueryStatus.exist = true;
      }

      if (e?.value?.Category !== undefined && e?.value?.Name !== undefined) {
        settingsQueryStatus.invalid = true;
      }
    });

    return settingsQueryStatus;
  };

  const enterViewNodesConfig = (data: NodeConfigInfo) => {
    setSelectedNodeConfig(data);
    setOpenNodesConfigPage(true);
  };

  const MyViewCommandCell = (props: GridCellProps) => (
    <ViewCommandCell {...props} enterViewNodesConfig={enterViewNodesConfig} />
  );

  const setAssetsDataToExport = async () => {
    const gridRowAssetConfigData: GridRowAssetTableInfo[] =
      flattenNodeConfigData(filteredAssetsData);
    setNodeConfigDataToExport(gridRowAssetConfigData);
  };

  const getCSVFileName = () => {
    return `configSettings_export_${dateFormat(currentLocale.language)}`;
  };

  const downloadNodesConfigData = async () => {
    exportFilteredAssetsConfig(dispatch, configSettingType);
  };

  const editNodesConfig = async () => {
    const selectedNodeUuidList: string[] = [];

    nodesConfigGridDataResult.map(result => {
      if (result.Selected) {
        selectedNodeUuidList.push(result.NodeInfo.Uuid);
      }
    });

    setSelectedNodeUuids(selectedNodeUuidList);
    toggleDialog();
  };

  const onVerticalChange = (event: SplitterOnChangeEvent) => {
    setVerticalPanes(event.newState);
  };

  const handlePageChange = (event: PageChangeEvent) => {
    const newDataState: State = {
      skip: event.skip,
      take: event.take,
      sort: activeDataState.sort,
      group: activeDataState.group,
    };

    saveActiveGridState(newDataState);

    setDataResult(processWithGroups(nodesConfigGridDataResult, newDataState));

    if (event.skip >= nodesConfigGridDataResult.length) {
      getAllNodesConfigByPage(dispatch, configSettingType);
    }
  };

  const pager = () => {
    return (
      <Pager
        skip={activeDataState.skip as number}
        take={activeDataState.take as number}
        total={nodeQueryResult.TotalRows}
        onPageChange={handlePageChange}
        buttonCount={10}
        info={true}
        previousNext={true}
        type="numeric"
        pageSizes={[10, 50, 100, 500, 1000]}
      />
    );
  };

  const dateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={activeDataState}
        fieldName="NodeInfo.LastHeartbeat"
      />
    );
  };

  const onSelectionChange = (event: GridSelectionChangeEvent) => {
    const newSelected = getSelectedState({
      event,
      selectedState: select,
      dataItemKey: DATA_ITEM_KEY,
    });

    setSelect(newSelected);

    const newData = nodesConfigGridDataResult.map(item => ({
      ...item,
      Selected: !!newSelected[item.NodeInfo.NodeID],
    }));

    getEditConfigButtonStatus(newData);
    setNodesConfigGridDataResult(newData);
  };

  const onHeaderSelectionChange = React.useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const checked = checkboxElement.checked;

      const newData = nodesConfigGridDataResult.map(item => ({
        ...item,
        Selected: checked,
      }));

      setSelect(event.select);
      getEditConfigButtonStatus(newData);

      const newDataResult = processWithGroups(newData, activeDataState);
      setDataResult(newDataResult);
      setNodesConfigGridDataResult(newData);
    },
    [nodesConfigGridDataResult, activeDataState],
  );

  const toggleDialog = () => {
    setEditDialogVisible(!editDialogVisible);
  };

  const getEditConfigButtonStatus = (data: NodesConfigGridResult[]) => {
    const selectedExist = data.findIndex(v => v.Selected);

    if (selectedExist === -1) {
      setEditConfigButtonEnabled(false);
    } else {
      setEditConfigButtonEnabled(true);
    }
  };

  const clearFetchError = () => {
    displayError(false);
    resetFetchError(dispatch);
  };

  const columnProps = (field: string) => {
    return {
      field,
      columnMenu: ColumnMenu,
      headerClassName: isColumnActive(field, activeDataState) ? 'active' : '',
    };
  };

  const isColumnActive = (field: string, gridState: State) => {
    return (
      isColumnMenuFilterActive(field, gridState.filter) ||
      isColumnMenuSortActive(field, gridState.sort)
    );
  };

  return (
    <div>
      {editDialogVisible && (
        <Dialog title={EDIT_CONFIG} onClose={toggleDialog}>
          <EditNodeConfigDetailComponent
            nodeSettings={nodeQueryResult.Result}
            selectedNodeUuids={selectedNodeUuids}
            configSettingType={configSettingType}
            toggleDialog={() => toggleDialog()}
          />
        </Dialog>
      )}
      {OpenNodesConfigPage ? (
        <NodeConfigFormContainer
          isPageOpened={OpenNodesConfigPage}
          configSettingType={configSettingType}
          handleClosePage={() => setOpenNodesConfigPage(false)}
          nodeConfigData={selectedNodeConfig}
        />
      ) : (
        <LocalizationProvider language={currentLocale.language}>
          <IntlProvider locale={currentLocale.locale}>
            <CSVLink
              data={nodeConfigDataToExport}
              ref={csvLinkRef}
              filename={getCSVFileName() + '.csv'}
            />
            <Splitter
              panes={verticalPanes}
              orientation={'vertical'}
              onChange={onVerticalChange}
            >
              <div>
                {!editDialogVisible && (
                  <NodeConfigFilter configSettingType={configSettingType} />
                )}
              </div>
              <div>
                {loading && LoadingPanel}
                <Grid
                  key={`${uniqueKeyOnRefresh}`}
                  style={{ height: '750px' }}
                  sortable={true}
                  filterable={true}
                  groupable={true}
                  reorderable={true}
                  resizable={true}
                  pageable={true}
                  fixedScroll={false}
                  pager={pager}
                  data={dataResult}
                  {...activeDataState}
                  onDataStateChange={dataStateChange}
                  dataItemKey={DATA_ITEM_KEY}
                  select={select}
                  selectable={{
                    enabled: true,
                    drag: true,
                    cell: false,
                    mode: selectionMode,
                  }}
                  onHeaderSelectionChange={onHeaderSelectionChange}
                  onSelectionChange={onSelectionChange}
                  cells={{
                    headerCell: HeaderCustomCell,
                    data: CustomCell,
                  }}
                >
                  <GridToolbar>
                    <Button onClick={onRunFilterNodesQuery}>
                      <StringResourceManager stringKey="RUN_QUERY" />
                    </Button>
                    <Button onClick={onGroupsToggle}>
                      <StringResourceManager
                        stringKey={
                          collapsedState.length
                            ? 'EXPAND_GROUPS'
                            : 'COLLAPSE_GROUPS'
                        }
                      />
                    </Button>
                    <Button onClick={downloadNodesConfigData}>
                      <StringResourceManager stringKey="DOWNLOAD_ASSETS" />
                    </Button>
                    <Button
                      onClick={editNodesConfig}
                      disabled={!editConfigButtonEnabled}
                    >
                      <StringResourceManager stringKey="EDIT_CONFIG" />
                    </Button>
                  </GridToolbar>
                  <GridToolbar>
                    <StringResourceManager
                      stringKey="LANE_COUNT"
                      variant="body1"
                    />
                    <Typography variant="body1" className={classes.laneCount}>
                      {nodeQueryResult.TotalRows}
                    </Typography>
                  </GridToolbar>
                  <GridColumn
                    filterable={false}
                    field={SELECTED_FIELD}
                    width={50}
                    columnType="checkbox"
                  />
                  <GridColumn
                    {...columnProps('NodeInfo.Division')}
                    title={COLUMN_TITLE_DIVISION}
                    width="110px"
                    columnMenu={ColumnMenu}
                    filterable={false}
                  />
                  <GridColumn
                    {...columnProps('NodeInfo.SiteNumber')}
                    title={COLUMN_TITLE_STORE}
                    width="110px"
                    columnMenu={ColumnMenu}
                    filterable={false}
                  />
                  <GridColumn
                    {...columnProps('NodeInfo.NodeGroup')}
                    title={COLUMN_TITLE_CLUSTER}
                    width="110px"
                    columnMenu={ColumnMenu}
                    filterable={false}
                  />
                  <GridColumn
                    {...columnProps('NodeInfo.LaneNumber')}
                    title={COLUMN_TITLE_LANE}
                    width="110px"
                    columnMenu={ColumnMenu}
                    filterable={false}
                  />
                  <GridColumn
                    {...columnProps('NodeInfo.NodeType')}
                    title={COLUMN_TITLE_LANE_TYPE}
                    width="130px"
                    columnMenu={props => (
                      <CustomColumnMenu
                        {...props}
                        dataState={activeDataState}
                      />
                    )}
                    filterable={false}
                  />
                  <GridColumn
                    cells={{ data: MyViewCommandCell }}
                    field="configuration"
                    title={COLUMN_TITLE_CONFIGURATION}
                    width="145px"
                    filterable={false}
                  />
                  <GridColumn
                    field="NodeInfo.LastHeartbeat"
                    title={COLUMN_TITLE_LANE_LAST_REFRESHED}
                    width="250px"
                    cells={{
                      data: DateTimeCell,
                      filterCell: dateFilterCell,
                    }}
                  />
                </Grid>
                <Fade>
                  <NotificationGroup className={classes.bottomRight}>
                    {editConfigStatus && (
                      <Notification
                        className={classes.send}
                        type={{
                          style: 'success',
                          icon: true,
                        }}
                        closable={true}
                        onClose={() => clearEditConfigSettings(dispatch)}
                      >
                        {
                          <StringResourceManager
                            stringKey={'COMMAND_SENT_SUCCESSFULLY'}
                          />
                        }
                      </Notification>
                    )}
                    {error && (
                      <Notification
                        className={classes.error}
                        type={{
                          style: 'error',
                          icon: true,
                        }}
                        closable={true}
                        onClose={() => clearFetchError()}
                      >
                        {queryError !== '' ? (
                          <StringResourceManager stringKey={queryError} />
                        ) : (
                          <div>
                            <StringResourceManager
                              stringKey={fetchError.Message}
                            />
                            {`(${status}: ${fetchError.StatusCode})`}
                          </div>
                        )}
                      </Notification>
                    )}
                  </NotificationGroup>
                </Fade>
              </div>
            </Splitter>
          </IntlProvider>
        </LocalizationProvider>
      )}
    </div>
  );
};

export default NodesConfigGrid;
